import React, { useEffect, useState } from 'react';
import { Download, TrendingUp, ShoppingCart, Bike, Clock, Store, Trash2, PackageCheck, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, Legend } from 'recharts';
import { money } from '../lib/constants';
import { useApp } from '../context/AppContext';
import api from '../lib/api';
import { PageHeader, Card, Stat, Table, Tr, Td } from './shared';
import { LiveBusinessOrders } from './LiveBusinessOrders';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

const DELIVERY_TIMES = [{ day: 'Lun', min: 24 }, { day: 'Mar', min: 22 }, { day: 'Mié', min: 27 }, { day: 'Jue', min: 25 }, { day: 'Vie', min: 30 }, { day: 'Sáb', min: 33 }, { day: 'Dom', min: 28 }];

const monthLabel = (m) => {
  const [y, mo] = (m || '').split('-');
  const names = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return mo ? `${names[Number(mo) - 1]} ${y}` : m;
};

const BusinessOrdersReport = () => {
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const load = async (m) => {
    setLoading(true);
    try { setData(await api.businessStats(m)); } catch (e) { toast.error('No se pudo cargar el reporte'); } finally { setLoading(false); }
  };

  useEffect(() => {
    api.businessStatsMonths().then((ms) => { setMonths(ms); setMonth(ms[0]); load(ms[0]); }).catch(() => {});
  }, []);

  const changeMonth = (m) => { setMonth(m); load(m); };

  const doReset = async () => {
    setResetting(true);
    try { await api.resetBusinessStats(); toast.success('Estadísticas reiniciadas'); const ms = await api.businessStatsMonths(); setMonths(ms); changeMonth(ms[0]); }
    catch (e) { toast.error('No se pudo reiniciar'); } finally { setResetting(false); }
  };

  const rows = data?.rows || [];
  const totalPickup = rows.reduce((s, r) => s + r.pickup, 0);
  const totalDelivery = rows.reduce((s, r) => s + r.delivery, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Select value={month} onValueChange={changeMonth}>
          <SelectTrigger data-testid="business-stats-month" className="w-48"><SelectValue placeholder="Mes" /></SelectTrigger>
          <SelectContent>{months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-2">
          <a data-testid="business-stats-pdf" href={api.businessStatsPdfUrl(month)} target="_blank" rel="noreferrer"><Button variant="outline" className="gap-2"><Download size={16} /> PDF</Button></a>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button data-testid="business-stats-reset-open" variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50"><Trash2 size={16} /> Reiniciar estadísticas</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>¿Reiniciar todas las estadísticas?</AlertDialogTitle><AlertDialogDescription>Se borrarán los conteos de Pickup/Delivery de TODOS los negocios y TODOS los meses. Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction data-testid="business-stats-reset-confirm" className="bg-[#EE3B36] hover:bg-[#D93129]" disabled={resetting} onClick={doReset}>{resetting ? 'Reiniciando...' : 'Sí, reiniciar'}</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
        <Stat label="Pickup este mes" value={totalPickup} icon={PackageCheck} bg="bg-[#E8F0FE]" iconBg="bg-[#D2E1FC] text-[#2F80ED]" sub={<p className="text-xs text-gray-500">recoger en tienda</p>} />
        <Stat label="Delivery este mes" value={totalDelivery} icon={Truck} bg="bg-[#E6F7F1]" iconBg="bg-[#CDEFE0] text-[#22A45D]" sub={<p className="text-xs text-gray-500">entregado a domicilio</p>} />
        <Stat label="Total de pedidos" value={totalPickup + totalDelivery} icon={Store} bg="bg-[#FDECEC]" iconBg="bg-[#F9D4D2] text-[#EE3B36]" sub={<p className="text-xs text-gray-500">{rows.length} negocios</p>} />
      </div>
      <Card title={`Pedidos por negocio - ${monthLabel(month)}`}>
        <div className="p-3">
          {loading ? <Skeleton className="h-64 rounded-xl" /> : (
            <Table columns={['Negocio', 'Categoría', 'Pickup', 'Delivery', 'Total']} rows={rows}
              renderRow={(r) => (
                <Tr key={r.businessId} data-testid={`business-stats-row-${r.businessId}`}>
                  <Td className="font-semibold">{r.name}</Td>
                  <Td className="capitalize text-gray-600">{r.category}</Td>
                  <Td className="font-semibold">{r.pickup}</Td>
                  <Td className="font-semibold">{r.delivery}</Td>
                  <Td className="font-extrabold">{r.total}</Td>
                </Tr>
              )} />
          )}
        </div>
      </Card>
    </div>
  );
};

const AdminReports = () => {
  const { restaurants, drivers, orders, stats } = useApp();
  if (!stats) return <Skeleton className="h-96 rounded-2xl" />;
  const t = stats.totals;
  // Revenue per week by type (from orders)
  const byType = stats.revenuePerWeek.map((w, idx) => {
    const end = new Date(); end.setDate(end.getDate() - (3 - idx) * 7); const start = new Date(end); start.setDate(end.getDate() - 6);
    const s = start.toISOString().slice(0, 10), e = end.toISOString().slice(0, 10);
    const inW = orders.filter((o) => o.status === 'completed' && o.date >= s && o.date <= e);
    return { week: w.week, Comida: inW.filter((o) => o.type === 'Comida').reduce((a, o) => a + o.total, 0), Mandaditos: inW.filter((o) => o.type === 'Mandadito').reduce((a, o) => a + o.total, 0), 'Envíos': inW.filter((o) => o.type === 'Envío').reduce((a, o) => a + o.total, 0) };
  });
  const restRows = restaurants.map((r) => { const ro = orders.filter((o) => o.restaurantId === r.id && o.status === 'completed'); const sales = ro.reduce((s, o) => s + o.total, 0); return { ...r, sales, count: ro.length, ticket: ro.length ? Math.round(sales / ro.length) : 0 }; }).sort((a, b) => b.sales - a.sales);
  const driverRows = drivers.filter((d) => d.approval === 'approved').map((d) => ({ ...d, earnings: orders.filter((o) => o.driverId === d.id && o.status === 'completed').reduce((s, o) => s + (o.driverFee || 0), 0) })).sort((a, b) => b.deliveries - a.deliveries);

  return (
    <div>
      <PageHeader title="Reportes" subtitle="Ventas, pagos y métricas operativas de Delibery 7" actions={<><Button variant="outline" className="gap-2" onClick={() => toast.success('Reporte PDF generado (demo)')}><Download size={16} /> PDF</Button><Button className="bg-[#EE3B36] hover:bg-[#D93129] gap-2" onClick={() => toast.success('Reporte Excel generado (demo)')}><Download size={16} /> Excel</Button></>} />
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <Stat label="Ventas del mes" value={money(t.revenue)} icon={TrendingUp} bg="bg-[#E6F7F1]" iconBg="bg-[#CDEFE0] text-[#22A45D]" sub={<p className="text-xs text-emerald-600 font-semibold">+{t.revenueGrowth}% vs. mes anterior</p>} />
        <Stat label="Ticket promedio" value={money(t.avgTicket)} icon={ShoppingCart} bg="bg-[#FDECEC]" iconBg="bg-[#F9D4D2] text-[#EE3B36]" sub={<p className="text-xs text-gray-500">por pedido completado</p>} />
        <Stat label="Tiempo prom. entrega" value="27 min" icon={Clock} bg="bg-[#E8F0FE]" iconBg="bg-[#D2E1FC] text-[#2F80ED]" sub={<p className="text-xs text-emerald-600 font-semibold">-3 min vs. mes anterior</p>} />
        <Stat label="Entregas completadas" value={t.completed} icon={Bike} bg="bg-[#F1ECFD]" iconBg="bg-[#E2D8FB] text-[#7C3AED]" sub={<p className="text-xs text-gray-500">{Math.round((t.completed / Math.max(1, t.orders)) * 100)}% tasa de éxito</p>} />
      </div>

      <Tabs defaultValue="ventas">
        <TabsList className="bg-white border border-gray-200 h-11 p-1 rounded-xl mb-4">
          <TabsTrigger value="ventas" className="rounded-lg px-5 data-[state=active]:bg-[#EE3B36] data-[state=active]:text-white">Ventas y pagos</TabsTrigger>
          <TabsTrigger value="operativo" className="rounded-lg px-5 data-[state=active]:bg-[#EE3B36] data-[state=active]:text-white">Operativo / Entregas</TabsTrigger>
          <TabsTrigger data-testid="tab-pedidos-negocio" value="negocios" className="rounded-lg px-5 data-[state=active]:bg-[#EE3B36] data-[state=active]:text-white">Pedidos por negocio</TabsTrigger>
          <TabsTrigger data-testid="tab-pickup-en-vivo" value="envivo" className="rounded-lg px-5 data-[state=active]:bg-[#EE3B36] data-[state=active]:text-white">Recoger vs domicilio (en vivo)</TabsTrigger>
        </TabsList>
        <TabsContent value="ventas" className="space-y-4">
          <div className="grid xl:grid-cols-2 gap-4">
            <Card title="Ingresos por tipo de servicio">
              <div className="h-[260px] px-2 pb-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byType} margin={{ top: 10, right: 12, left: -6, bottom: 0 }} barSize={22}>
                    <CartesianGrid vertical={false} stroke="#EEF0F3" />
                    <XAxis dataKey="week" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: '#F7F8FA' }} contentStyle={{ borderRadius: 12, border: '1px solid #eee', fontSize: 12 }} formatter={(v) => money(v)} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Comida" fill="#EE3B36" radius={[4, 4, 0, 0]} /><Bar dataKey="Mandaditos" fill="#2F80ED" radius={[4, 4, 0, 0]} /><Bar dataKey="Envíos" fill="#F5B400" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card title="Ingresos por semana">
              <div className="h-[260px] px-2 pb-3">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.revenuePerWeek} margin={{ top: 10, right: 12, left: -6, bottom: 0 }}>
                    <defs><linearGradient id="blueFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2F80ED" stopOpacity={0.3} /><stop offset="100%" stopColor="#2F80ED" stopOpacity={0.02} /></linearGradient></defs>
                    <CartesianGrid vertical={false} stroke="#EEF0F3" />
                    <XAxis dataKey="week" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #eee', fontSize: 12 }} formatter={(v) => money(v)} />
                    <Area type="monotone" dataKey="value" name="Ingresos" stroke="#2F80ED" strokeWidth={2.5} fill="url(#blueFill)" dot={{ r: 4, fill: '#2F80ED' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
          <Card title="Ventas por restaurante">
            <div className="p-3">
              <Table columns={['Restaurante', 'Categoría', 'Pedidos completados', 'Ventas (L)', 'Ticket promedio', 'Calificación']} rows={restRows}
                renderRow={(r) => (<Tr key={r.id}><Td><span className="flex items-center gap-2"><img src={r.image} alt="" className="w-8 h-8 rounded-lg object-cover" /><span className="font-semibold">{r.name}</span></span></Td><Td>{r.category}</Td><Td className="font-semibold">{r.count}</Td><Td className="font-semibold">{money(r.sales)}</Td><Td>{money(r.ticket)}</Td><Td>{r.rating}</Td></Tr>)} />
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="operativo" className="space-y-4">
          <div className="grid xl:grid-cols-2 gap-4">
            <Card title="Pedidos por día">
              <div className="h-[260px] px-2 pb-3">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.ordersPerDay} margin={{ top: 10, right: 12, left: -14, bottom: 0 }}>
                    <defs><linearGradient id="redFill2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#EE3B36" stopOpacity={0.25} /><stop offset="100%" stopColor="#EE3B36" stopOpacity={0.02} /></linearGradient></defs>
                    <CartesianGrid vertical={false} stroke="#EEF0F3" />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} interval={4} />
                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #eee', fontSize: 12 }} />
                    <Area type="linear" dataKey="value" name="Pedidos" stroke="#EE3B36" strokeWidth={2} fill="url(#redFill2)" dot={{ r: 3, fill: '#EE3B36' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card title="Tiempo promedio de entrega (min)">
              <div className="h-[260px] px-2 pb-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={DELIVERY_TIMES} margin={{ top: 10, right: 12, left: -14, bottom: 0 }} barSize={30}>
                    <CartesianGrid vertical={false} stroke="#EEF0F3" />
                    <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: '#F7F8FA' }} contentStyle={{ borderRadius: 12, border: '1px solid #eee', fontSize: 12 }} />
                    <Bar dataKey="min" name="Minutos" fill="#22A45D" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
          <Card title="Rendimiento de conductores">
            <div className="p-3">
              <Table columns={['Conductor', 'Entregas', 'Calificación', 'Ganancias (L)', 'Estado']} rows={driverRows}
                renderRow={(d) => (<Tr key={d.id}><Td><span className="flex items-center gap-2"><img src={d.avatar} alt="" className="w-8 h-8 rounded-full object-cover" /><span className="font-semibold">{d.name}</span></span></Td><Td className="font-semibold">{d.deliveries}</Td><Td>{d.rating}</Td><Td className="font-semibold">{money(d.earnings)}</Td><Td><span className={`text-xs font-semibold px-2 py-1 rounded-md ${d.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{d.status === 'active' ? 'Activo' : 'Inactivo'}</span></Td></Tr>)} />
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="negocios">
          <BusinessOrdersReport />
        </TabsContent>
        <TabsContent value="envivo">
          <LiveBusinessOrders />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminReports;
